/**
 * Superadmin: blogoverzicht — koppeling naar bewerken.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Calendar, PenLine, Plus } from 'lucide-react';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { SeoMeta } from '../../components/SeoMeta';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/AuthContext';
import { Navigate } from 'react-router-dom';

function fmtDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function AdminBlogList() {
  const { user } = useAuth();
  const isSuper = user?.role === 'superadmin';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-blog-posts'],
    queryFn: () => api.getAdminBlogPosts(),
    enabled: !!user?.authenticated && isSuper,
  });

  if (!isSuper) {
    return <Navigate to="/admin" replace />;
  }

  const posts = data?.posts ?? [];
  const errMsg = error instanceof Error ? error.message : '';

  return (
    <div>
      <SeoMeta path="/admin/cms/blog" noindex title="Blog (CMS)" description="Platformbeheer blogartikelen." />
      <Breadcrumbs
        items={[
          { label: 'Platformbeheer', href: '/admin' },
          { label: 'CMS', href: '/admin/cms' },
          { label: 'Blog' },
        ]}
      />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-text">Blog</h1>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-surface"
          >
            Vernieuwen
          </button>
          <Link
            to="/admin/cms/blog/nieuw"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-alt"
          >
            <Plus className="h-4 w-4" aria-hidden />
            Nieuw artikel
          </Link>
        </div>
      </div>
      <p className="mb-6 max-w-2xl text-sm text-text-muted">
        Alle velden (titel, SEO, HTML-inhoud, publicatiedatum) zijn hier te beheren. Wijzigingen zijn direct zichtbaar op
        de publieke <Link to="/blog">/blog</Link>.
      </p>

      {isLoading ? (
        <p className="text-text-muted">Laden…</p>
      ) : errMsg ? (
        <p className="text-red-600 dark:text-red-400">{errMsg}</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full min-w-[42rem] text-left text-sm">
            <thead className="border-b border-border bg-surface text-xs uppercase tracking-wide text-text-muted">
              <tr>
                <th className="px-3 py-2 font-semibold">Slug</th>
                <th className="px-3 py-2 font-semibold">Titel</th>
                <th className="px-3 py-2 font-semibold">Datum</th>
                <th className="px-3 py-2 font-semibold">Live</th>
                <th className="px-3 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.slug} className="border-b border-border/80 last:border-0 hover:bg-surface/80">
                  <td className="px-3 py-2 font-mono text-xs text-text-muted">{p.slug}</td>
                  <td className="max-w-[14rem] truncate px-3 py-2 font-medium text-text">{p.title}</td>
                  <td className="whitespace-nowrap px-3 py-2 text-text-muted">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3.5 w-3.5" aria-hidden />
                      {fmtDate(p.published_at)}
                    </span>
                  </td>
                  <td className="px-3 py-2">{p.published ? 'ja' : 'nee'}</td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/admin/cms/blog/${encodeURIComponent(p.slug)}`}
                      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
                    >
                      <PenLine className="h-3.5 w-3.5" aria-hidden />
                      Bewerken
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
